import _defineProperty from "@babel/runtime/helpers/defineProperty";
/* eslint-disable max-lines */
import React, { Component } from 'react';
import isMatch from 'lodash/isMatch';
import once from 'lodash/once';
import PropTypes from 'prop-types';
import window from 'window-or-global';
import { AnalyticsEvent, AnalyticsListener } from '@atlaskit/analytics-next';
import { startMessage, stopMessage } from '../../services/engage-targeting';
import { sendOperationalEvent, sendScreenEvent, sendUIEvent } from '../../services/gasv3';
import { getBrowserInfo } from '../../utils/browser';
import createCustomEvent from '../../utils/createCustomEvent';
import { isMessagingEnabled } from '../../utils/messaging';
import { clearCurrent, setCurrent } from '../engagement-actions';
import Queue from '../queue';
const MAX_EVENT_QUEUE_SIZE = 100;
const EK_LOADED_EVENT = 'engagement.inproduct.loaded';
export const ANALYTIC_EVENT_DOM_EVENT_NAME = 'ep.analyticEvent';
function collapseEventToPayload(event) {
  const payload = {
    ...event.payload,
    context: {}
  };
  Object.assign(payload.context, ...event.context);
  return payload;
}
function findMatchingTrigger(messageMapping, payload) {
  return Array.from(messageMapping.keys()).find(trigger => {
    return isMatch(payload, trigger);
  });
}
function fireExposureEvent(attributes, clientOrPromise) {
  const analyticsEvent = new AnalyticsEvent({
    payload: {
      action: 'exposed',
      actionSubject: 'feature',
      actionSubjectId: attributes.flagKey,
      source: 'engagement-platform',
      attributes
    }
  });
  Promise.resolve(clientOrPromise).then(client => {
    client.sendTrackEvent(analyticsEvent.payload);
  });
}
function getExposureAttributes(messageId, cohort, additionalProps) {
  return {
    reason: 'RULE_MATCH',
    ruleId: 'engagement.inproduct',
    flagKey: `engagement.inproduct.${messageId}`,
    value: cohort,
    ...additionalProps
  };
}
function getBaseAttributes(messageId, variationId, componentId, locale) {
  return {
    messageId: messageId,
    variationId: variationId,
    componentIndex: componentId,
    locale: locale
  };
}

// TODO refactor this to be inside EngagementProvider
// Instead of calling out to fireAnalyticsEvent we can call this.fireEvent
export async function triggerShown(eventMapping, config, store, clientOrPromise) {
  const {
    locale
  } = config;
  const {
    message,
    component
  } = eventMapping;
  if (message.holdout) {
    await startMessage({
      cloudId: config.cloudId,
      stargateUrl: config.stargateUrl,
      messageId: message.messageId,
      variationId: message.variationId,
      orgId: config.orgId,
      workspaceId: config.workspaceId
    });
    await stopMessage({
      cloudId: config.cloudId,
      stargateUrl: config.stargateUrl,
      messageId: message.messageId,
      orgId: config.orgId,
      workspaceId: config.workspaceId
    });
    fireExposureEvent(getExposureAttributes(message.messageId, 'holdout', {
      locale
    }), clientOrPromise);
    return;
  }
  if (component.componentId === 0) {
    const success = await startMessage({
      cloudId: config.cloudId,
      stargateUrl: config.stargateUrl,
      messageId: message.messageId,
      variationId: message.variationId,
      orgId: config.orgId,
      workspaceId: config.workspaceId
    });
    if (success) {
      fireExposureEvent(getExposureAttributes(message.messageId, message.variationId, {
        locale
      }), clientOrPromise);
      store.dispatch(setCurrent(eventMapping));
    }
  } else {
    fireExposureEvent(getExposureAttributes(message.messageId, message.variationId, {
      locale
    }), clientOrPromise);
    store.dispatch(setCurrent(eventMapping));
  }
}
// eslint-disable-next-line @repo/internal/react/no-class-components
export class EngagementProvider extends Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onStoreChange", state => {
      const {
        createAnalyticsEvent
      } = this.props;
      const config = this.getConfig();
      const {
        locale
      } = config;
      if (state.initialized) {
        if (!EngagementProvider.hasInitializedAtLeastOneEngagementProvider) {
          this.sendOperationalEventWithClient({
            action: 'loaded',
            actionSubject: 'messages',
            actionSubjectId: undefined,
            source: 'engagekit',
            attributes: {
              locale
            }
          });
          const browserInfo = getBrowserInfo();
          this.onEvent(createAnalyticsEvent({
            event: EK_LOADED_EVENT,
            properties: {
              browser: browserInfo.name,
              browserVersion: browserInfo.version
            }
          }));
          EngagementProvider.hasInitializedAtLeastOneEngagementProvider = true;
        }
        this.analyticsEventQueue.flush(this.onEvent);
        if (this.unsubscribeFromStore) {
          this.unsubscribeFromStore();
        }
      }
    });
    _defineProperty(this, "onEvent", event => {
      const state = this.getState();
      if (!state.initialized) {
        const success = this.analyticsEventQueue.enqueue(event);
        if (!success) {
          this.fireQueueOverflowEvent();
        }
        return;
      }
      const {
        eventTarget,
        store,
        analyticsClientInstance
      } = this.props;
      const config = this.getConfig();
      const {
        current,
        messageMapping
      } = state;
      const payload = collapseEventToPayload(event);
      eventTarget.dispatchEvent(createCustomEvent({
        eventName: ANALYTIC_EVENT_DOM_EVENT_NAME,
        canBubble: false,
        cancellable: false,
        detail: payload
      }));
      if (!isMessagingEnabled()) {
        return;
      }

      // existing message found
      if (current) {
        return;
      }
      const trigger = findMatchingTrigger(messageMapping, payload);

      // No messages found for trigger
      if (!trigger) {
        return;
      }
      triggerShown(messageMapping.get(trigger), config, store, analyticsClientInstance);
    });
    _defineProperty(this, "onEngagementEvent", event => {
      const {
        action,
        clickType,
        ekMessageId,
        ekVariationId,
        ekComponentId,
        ekComponentType,
        ekIsLast
      } = event.payload;
      const config = this.getConfig();
      const {
        locale
      } = config;
      const attributes = getBaseAttributes(ekMessageId, ekVariationId, ekComponentId, locale);
      if (action === 'click') {
        this.handleClickEvent(ekMessageId, ekVariationId, ekComponentId);
        attributes.buttonType = clickType;
        this.sendUIEventWithClient({
          action: 'clicked',
          actionSubject: 'button',
          actionSubjectId: `${clickType}Button`,
          source: `engagement_${ekComponentType}`,
          attributes
        });
      } else {
        if (action === 'shown') {
          attributes.componentType = ekComponentType;
          this.sendScreenEventWithClient({
            name: `engagement_${ekComponentType}`,
            attributes
          });
        }
        if (action === 'hidden') {
          attributes.isLast = ekIsLast;
          this.sendUIEventWithClient({
            action: 'hid',
            actionSubject: 'component',
            actionSubjectId: undefined,
            source: `engagement_${ekComponentType}`,
            attributes
          });
        }
      }
      this.onEvent(event);
    });
    _defineProperty(this, "subscribeEngagementState", callback => {
      const {
        store
      } = this.props;
      callback(store.getState());
      return store.subscribe(() => {
        callback(store.getState());
      });
    });
    const {
      analyticsClientInstance: _analyticsClientInstance
    } = this.props;
    this.sendUIEventWithClient = sendUIEvent.bind(null, _analyticsClientInstance);
    this.sendScreenEventWithClient = sendScreenEvent.bind(null, _analyticsClientInstance);
    this.sendOperationalEventWithClient = sendOperationalEvent.bind(null, _analyticsClientInstance);
    this.analyticsEventQueue = new Queue(MAX_EVENT_QUEUE_SIZE);
    this.unsubscribeFromStore = this.subscribeEngagementState(this.onStoreChange);
    this.fireQueueOverflowEvent = once(
    // TODO: create metric for this with Metal
    () => {});
  }
  getConfig() {
    const {
      config
    } = this.getState();
    return config;
  }
  getChildContext() {
    return {
      subscribeEngagementState: this.subscribeEngagementState
    };
  }
  getState() {
    return this.props.store.getState();
  }
  getActiveMessage() {
    return this.getState().current;
  }
  completeMessage(messageId) {
    const config = this.getConfig();
    stopMessage({
      cloudId: config.cloudId,
      stargateUrl: config.stargateUrl,
      messageId,
      orgId: config.orgId,
      workspaceId: config.workspaceId
    });
  }
  handleClickEvent(messageId, variationId, componentId) {
    const activeMessage = this.getActiveMessage();

    // It is important to check that the event we're handling matches the
    // current active message because it is possible for an event to come
    // through for a past message. We don't want to mess up the current
    // message by calling clearCurrent just because the old message is done.
    if (!activeMessage || activeMessage.messageId !== messageId || activeMessage.variationId !== variationId || activeMessage.componentId !== componentId) {
      return;
    }
    if (activeMessage.isLast) {
      this.completeMessage(messageId);
    }
    this.props.store.dispatch(clearCurrent());
  }
  componentWillUnmount() {
    this.unsubscribeFromStore && this.unsubscribeFromStore();
  }
  render() {
    return /*#__PURE__*/React.createElement(AnalyticsListener, {
      channel: "*",
      onEvent: this.onEvent
    }, /*#__PURE__*/React.createElement(AnalyticsListener, {
      channel: "engagement",
      onEvent: this.onEngagementEvent
    }, this.props.children));
  }
}
_defineProperty(EngagementProvider, "childContextTypes", {
  subscribeEngagementState: PropTypes.func
});
_defineProperty(EngagementProvider, "defaultProps", {
  eventTarget: window
});
/*
 * We use this flag for ensuring that we only fire the engagement.inproduct.loaded event once. This is an interim solution
 * until we can more holistically rethink our code architecture to eliminate duplicate behaviour across EngagementProviders.
 */
_defineProperty(EngagementProvider, "hasInitializedAtLeastOneEngagementProvider", false);